import { FONT, T, PETROL } from "../theme";

// ═══ TOPIC ANALYSIS CARD ═══════════════════════════════════════════════════════
// A data card that sits beside the Article Research wall: it breaks the 234-article
// corpus into topics, ranks each by frequency (horizontal bars, width ∝ count),
// then splits saturated-vs-whitespace and calls out OUR lane. Design law:
// bone/ink, hairline rules, one typeface (Inter). Color has ONE job — PETROL marks
// the emphasis (THE GAP / our lane); every other bar is a neutral warm grey.

type Bar = { rank: number; label: string; note?: string; count: number; pct: string; gap?: boolean };
type Split = { heading: string; note?: string; items: string[] };

export function AnalysisCardNode({
  data,
}: {
  data: {
    title: string;
    sub: string;
    maxCount: number;
    bars: Bar[];
    saturated: Split;
    whitespace: Split;
    lane: { heading: string; body: string; bold: string };
    w?: number;
  };
}) {
  const W = data.w ?? 1040;
  const BAR_MAX = 360; // px for the biggest count; the rest scale ∝ count
  const BAR_GREY = "#B4A895"; // neutral warm grey — the saturated topics
  const HAIRLINE = "#F0EAE1";

  return (
    <div
      style={{
        fontFamily: FONT,
        width: W,
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 20,
        boxShadow: "0 24px 60px rgba(0,0,0,0.12)",
        padding: 44,
      }}
    >
      {/* eyebrow + header */}
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: PETROL, marginBottom: 14 }}>
        Article research · synthesis
      </div>
      <div style={{ fontSize: 52, fontWeight: 800, letterSpacing: "-0.03em", color: T.ink, lineHeight: 1 }}>{data.title}</div>
      <div style={{ marginTop: 14, fontSize: 21, fontWeight: 500, color: T.ink2, lineHeight: 1.4 }}>{data.sub}</div>

      <div style={{ marginTop: 28, height: 1, background: T.line }} />

      {/* ranked frequency bars */}
      <div style={{ marginTop: 22 }}>
        {data.bars.map((b) => {
          const accent = !!b.gap;
          const barW = Math.max(7, (b.count / data.maxCount) * BAR_MAX);
          return (
            <div
              key={b.rank}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "10px 0",
                borderTop: b.rank === 1 ? "none" : `1px solid ${HAIRLINE}`,
              }}
            >
              <span
                style={{
                  width: 34,
                  flexShrink: 0,
                  fontSize: 15,
                  fontWeight: 700,
                  color: accent ? PETROL : "#A79C8B",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {b.rank}
              </span>
              <div style={{ width: 372, flexShrink: 0, paddingRight: 20 }}>
                <div style={{ fontSize: 17, fontWeight: accent ? 800 : 600, color: accent ? PETROL : T.ink, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
                  {b.label}
                  {accent && (
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        color: T.white,
                        background: PETROL,
                        borderRadius: 999,
                        padding: "3px 9px",
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                      }}
                    >
                      THE GAP
                    </span>
                  )}
                </div>
                {b.note && <div style={{ marginTop: 3, fontSize: 12.5, color: "#9A8F7E", lineHeight: 1.3 }}>{b.note}</div>}
              </div>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                <div style={{ height: 20, width: barW, flexShrink: 0, background: accent ? PETROL : BAR_GREY, borderRadius: 5 }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: accent ? PETROL : T.ink, fontVariantNumeric: "tabular-nums" }}>{b.count}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: T.ink2, fontVariantNumeric: "tabular-nums" }}>{b.pct}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* saturated vs white-space split */}
      <div style={{ marginTop: 34, display: "flex", gap: 26 }}>
        {[data.saturated, data.whitespace].map((s, i) => {
          const ours = i === 1;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: ours ? "rgba(43,85,96,0.06)" : "#F7F2EA",
                border: `1px solid ${ours ? "rgba(43,85,96,0.28)" : T.line}`,
                borderRadius: 14,
                padding: 26,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: ours ? PETROL : "#8A7F6E" }}>{s.heading}</div>
              {s.note && <div style={{ marginTop: 7, fontSize: 14, color: T.ink2, lineHeight: 1.4 }}>{s.note}</div>}
              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 11 }}>
                {s.items.map((it, k) => (
                  <div key={k} style={{ display: "flex", gap: 12 }}>
                    <span style={{ marginTop: 8, flexShrink: 0, width: 7, height: 7, borderRadius: "50%", background: ours ? PETROL : "#C0B4A0" }} />
                    <span style={{ fontSize: 16, fontWeight: ours ? 700 : 500, color: ours ? T.ink : T.ink2, lineHeight: 1.35 }}>{it}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* OUR LANE callout — the one petrol-filled block */}
      <div style={{ marginTop: 26, background: PETROL, borderRadius: 16, padding: 32 }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", marginBottom: 12 }}>{data.lane.heading}</div>
        <div style={{ fontSize: 18, color: "rgba(255,255,255,0.92)", lineHeight: 1.5 }}>{data.lane.body}</div>
        <div style={{ marginTop: 14, fontSize: 19, fontWeight: 800, color: T.white, lineHeight: 1.4, letterSpacing: "-0.01em" }}>{data.lane.bold}</div>
      </div>
    </div>
  );
}
